import {
  Accordion,
  DrawerForm,
  DrawerSizeEnum,
  PlusIcon,
  SupportRadioIcon,
  SupportTvIcon,
} from '@acceso/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckboxForm } from '@/components/FormFields/CheckboxForm';
import { InputDate } from '@/components/FormFields/InputDate';
import { InputStatusSeenChipSelect } from '@/components/FormFields/InputStatusSeenChipSelect';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { PRIORITY } from '@/features/broadcast-schedule/constants';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { useAudienceProgramSourceOptions } from '@/features/broadcasters/hooks/useAudienceProgramSourceOptions';
import { ChannelMediaTypeEnum, ChannelTypeEnum } from '@/features/broadcasters/types';
import { useLanguageOptions } from '@/features/languages/hooks/useLanguageOptions';
import { useGetPrograms } from '@/features/programs/api/get-programs';
import { TProgram } from '@/features/programs/types';
import { FormTypeEnum } from '@/types';
import { BaseDrawerFormProps } from '@/types/drawer-forms';
import { applyScheduleHour } from '@/utils/date';

import { useProgramBroadcastController } from '../../hooks/useProgramBroadcastController';
import {
  useProgramBroadcastDrawerFormState,
  PROGRAM_BROADCAST_FORM_DEFAULT_VALUES,
} from '../../hooks/useProgramBroadcastDrawerFormState';
import { useProgramBroadcastOptions } from '../../hooks/useProgramBroadcastOptions';
import { useProgramOptions } from '../../hooks/useProgramOptions';
import { ProgramBroadcastDrawerFormData, TProgramBroadcastForm } from '../../types';
import { ProgramDrawerForm } from '../ProgramDrawerForm/ProgramDrawerForm';

type ProgramBroadcastDrawerFormProps = BaseDrawerFormProps<
  ProgramBroadcastDrawerFormData,
  TProgramBroadcastForm
> & {
  audiencesUsed?: string[];
};

const DEFAULT_AUDIENCES_USED = [];
// Stable, so the effects that depend on the programmes do not run on every
// render while they load.
const NO_PROGRAMS: TProgram[] = [];

/**
 * Mounts the form, and with it the requests for its data, only while the
 * drawer is open or animating out. The Parrilla mounts it on load, and most
 * visits never open it.
 */
export function ProgramBroadcastDrawerForm(props: ProgramBroadcastDrawerFormProps) {
  const [isMounted, setIsMounted] = useState(props.isOpen);

  // Set while rendering rather than in an effect, so the content mounts in the
  // render that opens the drawer, and an exit that ends after it has been
  // opened again is undone before anything is committed.
  if (props.isOpen && !isMounted) {
    setIsMounted(true);
  }

  if (!props.isOpen && !isMounted) {
    return null;
  }

  return (
    <ProgramBroadcastDrawerFormContent {...props} onExitComplete={() => setIsMounted(false)} />
  );
}

function ProgramBroadcastDrawerFormContent({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
  audiencesUsed = DEFAULT_AUDIENCES_USED,
  onExitComplete,
}: ProgramBroadcastDrawerFormProps & { onExitComplete: () => void }) {
  const { t } = useTranslation();
  const { form } = useProgramBroadcastDrawerFormState();

  // Declared before the effects below that reset the form: `useWatch`
  // subscribes in an effect of its own, and the form mounts with its data, so
  // a reset that ran first would leave these values at their defaults.
  const { audience_program_source_id: audienceProgramSourceId } = useWatch({
    control: form.control,
  });
  const { program_id: programId, channel_id: channelId } = useWatch({
    control: form.control,
  });

  // removed reset-on-close; we now reset on drawer 'data' changes like Programs does

  useEffect(() => {
    const formData = data?.data?.programBrocast;

    if (formData) {
      form.reset({
        ...formData,
        start_datetime: new Date(formData.start_datetime),
        end_datetime: new Date(formData.end_datetime),
        delivery_datetime: formData.delivery_datetime
          ? new Date(formData.delivery_datetime)
          : undefined,
      });
    }
  }, [data]);

  useEffect(() => {
    if (data?.type === FormTypeEnum.CREATE) {
      form.reset(PROGRAM_BROADCAST_FORM_DEFAULT_VALUES as TProgramBroadcastForm);
    }
  }, [data?.type]);

  const priorityOptions = useMemo(
    () => PRIORITY.map(priority => ({ label: priority.render, value: priority.value })),
    []
  );

  const languageOptions = useLanguageOptions();

  const [audienceName, setAudienceName] = useState('');
  useEffect(() => {
    if (isOpen) {
      setAudienceName('');
    }
  }, [isOpen]);

  const includeAudienceProgramSourceIds = useMemo(
    () => (audienceProgramSourceId ? [audienceProgramSourceId] : undefined),
    [audienceProgramSourceId]
  );

  const audienceProgramSourceOptions = useAudienceProgramSourceOptions({
    name: audienceName,
    includeIds: includeAudienceProgramSourceIds,
  });

  const handleClose = () => {
    form.reset(PROGRAM_BROADCAST_FORM_DEFAULT_VALUES as TProgramBroadcastForm);
    onClose();
  };

  const handleSubmit = form.handleSubmit(onSubmit);

  const [isOpenDumpProgramBroadcastAccoridion, setIsOpenDumpProgramBroadcastAccoridion] =
    useState(false);

  const audiences = data?.data?.audiences ?? [];
  const nextAudience = audiences.at(audiencesUsed.length);

  const { data: { data: programs = NO_PROGRAMS } = {}, isSuccess: areProgramsLoaded } =
    useGetPrograms();
  const program = programs.find(program => program.id === programId);

  const programBroadcastOptions = useProgramBroadcastOptions({
    params: {
      limit: 25,
      channelId: program?.channel.id,
    },
  });
  const programOptions = useProgramOptions(channelId);

  const channelOptions = useChannels({});
  const allChannels = channelOptions.data?.data;
  const areChannelsLoaded = channelOptions.isSuccess;
  const channel = allChannels?.find(channel => channel.id === program?.channel.id);

  // A channel group is the parent (root) channel plus its territorial
  // disconnections. When the selected channel is itself a root (no
  // parent_channel_id) its own id is the group root; when it is a disconnection
  // the root is its parent. We must always include the root so the channel that
  // gets stored in `channel_id` (which can be a root channel) is selectable.
  const groupRootId = channel?.parent_channel_id ?? channel?.id;
  const childChannelsOptions = useMemo(() => {
    if (!groupRootId || !allChannels) return [];

    return allChannels
      .filter(channel => channel.id === groupRootId || channel.parent_channel_id === groupRootId)
      .map(newChannel => {
        return {
          value: newChannel.id,
          label:
            newChannel.type === ChannelTypeEnum.BACKUP
              ? newChannel.name + ' (' + newChannel.short_name + ') (Backup)'
              : newChannel.name + ' (' + newChannel.short_name + ')',
          image: newChannel.parameters?.logo_presigned_url
            ? newChannel.parameters?.logo_presigned_url
            : '/default-logo.svg',
          icon: newChannel.media_type.includes(ChannelMediaTypeEnum.TV)
            ? SupportTvIcon
            : SupportRadioIcon,
        };
      });
  }, [allChannels, groupRootId]);

  /**
   * Sets the channel id from the new selected program.
   */
  useEffect(() => {
    // Closing clears `data` without resetting the form, so the saved
    // broadcast's programme would look newly picked and overwrite what the
    // drawer shows while it animates out.
    if (!program || !data) return;
    // The programme was watched in this render, and a reset from new data may
    // have replaced it since. Acting on it would copy the previous broadcast's
    // programme over the new one; the next render sees the current value.
    if (form.getValues('program_id') !== program.id) return;

    if (program.id !== data?.data?.programBrocast?.program_id) {
      form.setValue('channel_id', program.channel.id);
      form.setValue('priority', program.priority);
      form.setValue('language_id', program.language_id);
      form.setValue('audience_program_source_id', program.audience_program_source_id);
      form.setValue('name', program.name);
      form.setValue('description', program.description);
      const schedule = program.schedule?.[0];
      const today = new Date();
      // Schedule hours are Spanish wall clock, placed on today's Madrid day.
      const startDatetime = applyScheduleHour(today, schedule?.start_hour);
      const sameDayEnd = applyScheduleHour(today, schedule?.end_hour);
      // A slot crossing midnight ends on the following Madrid day.
      const endDatetime =
        startDatetime > sameDayEnd ? applyScheduleHour(today, schedule?.end_hour, 1) : sameDayEnd;

      if (program.start_datetime) {
        form.setValue('start_datetime', startDatetime);
      }
      if (program.end_datetime) {
        form.setValue('end_datetime', endDatetime);
      }
    } else if (channel) {
      // Not before the channels arrive: an undefined channel would leave the
      // form unsavable until then.
      form.setValue('channel_id', channel.id);
    }
  }, [
    form.setValue,
    form.getValues,
    program?.id,
    data?.data?.programBrocast?.program_id,
    channel?.id,
  ]);

  /**
   * Reverse of the effect above (Emisora -> Programa).
   * When the channel changes and the current program does not belong to it, we
   * auto-select the program when the channel has a single one, otherwise we clear
   * the selection so the user picks a valid program from the filtered list.
   */
  useEffect(() => {
    // Until the programmes arrive, a program missing from them cannot be told
    // apart from one on another channel, and clearing it would lose the one
    // being edited. Until the channels arrive, the effect above has not moved
    // the channel of a broadcast on a territorial disconnection to its
    // programme's, and the programme would be replaced by the disconnection's.
    if (!areProgramsLoaded || !areChannelsLoaded || !channelId) return;
    // the current program already belongs to the selected channel: nothing to do
    if (program?.channel.id === channelId) return;
    // The channel was watched in this render, and the effect above may have
    // moved it since. Acting on the old one would replace or clear the programme
    // being edited; the next render sees the current value.
    if (form.getValues('channel_id') !== channelId) return;

    const channelPrograms = programs.filter(program => program.channel.id === channelId);
    form.setValue('program_id', channelPrograms.length === 1 ? channelPrograms[0].id : '');
  }, [
    form.setValue,
    form.getValues,
    areProgramsLoaded,
    areChannelsLoaded,
    channelId,
    program?.channel.id,
    programs,
  ]);

  const { programDrawer, isCreatingProgram, handleCreateProgram } = useProgramBroadcastController({
    onProgramCreated: program => {
      // the new program created is assigned in the form so the select can render it
      form.setValue('program_id', program.id);
    },
  });
  const [programFilterText, setProgramFilterText] = useState('');

  const hasMultipleAudiences = audiences.length > 1;
  const isLastAudience = audiences.length === audiencesUsed.length + 1;

  useEffect(() => {
    if (nextAudience) {
      // when we create a new program broadcast from an audience file then we reset
      // so the next audience file can be used to create the next program broadcast
      form.reset({
        ...PROGRAM_BROADCAST_FORM_DEFAULT_VALUES,
        name: nextAudience.program_source?.name,
        audience_program_source_id: nextAudience.program_source_id,
        audience_id: nextAudience.id,
      } as TProgramBroadcastForm);
    }
  }, [nextAudience, form.reset]);

  return (
    <>
      <DrawerForm
        title={
          data?.type === FormTypeEnum.CREATE
            ? t('Crear nueva emisión')
            : data?.type === FormTypeEnum.UPDATE
              ? t('Editar emisión')
              : t('Duplicar emisión')
        }
        steps={
          audiences.length > 1
            ? {
                activeStep: audiencesUsed.length + 1,
                total: audiences.length,
              }
            : undefined
        }
        isOpen={isOpen}
        onExitComplete={onExitComplete}
        onCancel={handleClose}
        onSubmit={handleSubmit}
        size={DrawerSizeEnum.MD}
        isMutating={isMutating}
        labels={{
          submit: hasMultipleAudiences && !isLastAudience ? t('Crear y continuar') : t('Guardar'),
          cancel: t('Cancelar'),
        }}
      >
        <InputStatusSeenChipSelect form={form} name="review_state" required />

        <RowContainer>
          <SelectForm
            form={form}
            name="program_id"
            required
            label={t('Programa')}
            placeholder={t('Seleccionar...')}
            options={programOptions}
            onFilterTextChange={setProgramFilterText}
            additionalButtons={[
              {
                label: t('Crear nuevo programa'),
                icon: PlusIcon,
                onClick: () =>
                  programDrawer.open({
                    type: FormTypeEnum.CREATE,
                    data: { name: programFilterText },
                  }),
              },
            ]}
          />

          <InputText
            form={form}
            name="name"
            required
            label={t('Nombre')}
            placeholder={t('Escribe aquí el nombre')}
          />
        </RowContainer>

        <RowContainer>
          <SelectForm
            form={form}
            name="channel_id"
            required
            label={t('Emisora')}
            options={childChannelsOptions}
          />

          <InputText form={form} name="description" required label={t('Descripción')} />
        </RowContainer>

        <RowContainer>
          <InputDate
            form={form}
            name="start_datetime"
            label={t('Fecha y hora inicio')}
            showTimeField={true}
            required
          />

          <InputDate
            form={form}
            name="end_datetime"
            label={t('Fecha y hora fin')}
            showTimeField={true}
            required
          />
        </RowContainer>

        <RowContainer>
          <InputDate
            form={form}
            name="delivery_datetime"
            label={t('Fecha y hora entrega')}
            required
            showTimeField={true}
          />

          <SelectForm
            form={form}
            name="priority"
            required
            label={t('Prioridad')}
            options={priorityOptions as unknown as { label: string; value: number }[]}
          />
        </RowContainer>

        <RowContainer>
          <SelectForm
            form={form}
            name="language_id"
            required
            label={t('Idioma')}
            options={languageOptions}
          />

          <SelectForm
            form={form}
            name="audience_program_source_id"
            label={t('Audiencia')}
            options={audienceProgramSourceOptions}
            onFilterTextChange={setAudienceName}
            withFilter
          />
        </RowContainer>

        <CheckboxForm form={form} name="rebroadcast" label={t('Redifusión')} />

        <Accordion
          title={t('Volcado de contenido')}
          isOpen={isOpenDumpProgramBroadcastAccoridion}
          onOpen={() =>
            setIsOpenDumpProgramBroadcastAccoridion(!isOpenDumpProgramBroadcastAccoridion)
          }
          lineSeparator
        >
          <SelectForm
            form={form}
            name="dump_program_broadcast_id"
            label={t('Emisión de programa')}
            options={programBroadcastOptions}
          />
        </Accordion>
      </DrawerForm>

      <ProgramDrawerForm
        isOpen={programDrawer.isOpen}
        data={programDrawer.data}
        isMutating={isCreatingProgram}
        onSubmit={handleCreateProgram}
        onClose={programDrawer.close}
      />
    </>
  );
}
